// src/components/question/QuestionLimitModal.tsx

import React, { useEffect, useRef } from 'react';
import { LockKeyhole, ExternalLink, ArrowLeft, RefreshCw } from 'lucide-react';
import './QuestionLimitModal.css';

type LimitReason = 'daily_exhausted' | 'total_exhausted' | 'not_free_question' | null;

interface QuestionLimitModalProps {
    isOpen: boolean;
    reason: LimitReason;
    onClose: () => void;
}

const CONTENT: Record<NonNullable<LimitReason>, { title: string; description: string; icon: React.ReactNode }> = {
    daily_exhausted: {
        title: 'Limite diário atingido',
        description: 'Você atingiu o limite de questões gratuitas de hoje. Volte amanhã para continuar, ou adquira o plano completo para acesso ilimitado ao banco de questões.',
        icon: <RefreshCw size={34} strokeWidth={1.6} />,
    },
    total_exhausted: {
        title: 'Amostra encerrada',
        description: 'Você utilizou todas as questões gratuitas disponíveis. Adquira o plano completo para continuar respondendo todo o banco de questões.',
        icon: <LockKeyhole size={34} strokeWidth={1.6} />,
    },
    not_free_question: {
        title: 'Questão bloqueada',
        description: 'Esta questão não faz parte da amostra gratuita. Adquira o plano completo para acessar todo o banco de questões.',
        icon: <LockKeyhole size={34} strokeWidth={1.6} />,
    },
};

const QuestionLimitModal: React.FC<QuestionLimitModalProps> = ({ isOpen, reason, onClose }) => {
    const primaryBtnRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isOpen) setTimeout(() => primaryBtnRef.current?.focus(), 50);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen || !reason) return null;

    if (!isOpen || !reason) return null;
    const content = CONTENT[reason];
    if (!content) return null;

    return (
        <div
            className="qlm-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="qlm-title"
            aria-describedby="qlm-desc"
        >
            <div className="qlm-card">
                <div className="qlm-icon-wrap">
                    {content.icon}
                </div>

                <h2 id="qlm-title" className="qlm-title">
                    {content.title}
                </h2>

                <p id="qlm-desc" className="qlm-desc">
                    {content.description}
                </p>

                <div className="qlm-actions">
                    <button
                        ref={primaryBtnRef}
                        className="qlm-btn qlm-btn--primary"
                        onClick={() => window.open('/marketplace', '_blank', 'noopener,noreferrer')}
                    >
                        <ExternalLink size={15} strokeWidth={2} />
                        Ver planos
                    </button>

                    <button
                        className="qlm-btn qlm-btn--secondary"
                        onClick={onClose}
                    >
                        <ArrowLeft size={15} strokeWidth={2} />
                        Continuar navegando
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuestionLimitModal;